import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { ErrorPage, LoadingPage } from '@/components/primitives';
import { RunsTable } from '@/components/RunsTable';
import { useRuns, useWorkflows } from '@/hooks/useSturmData';
import { runsApi } from '@/lib/apiClient';
import type { RunStatus } from '@/types/sturm';

const STATUSES: (RunStatus | 'all')[] = ['all', 'running', 'failed', 'completed', 'timedout'];

export function AllRunsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<RunStatus | 'all'>('all');
  const [workflow, setWorkflow] = useState('all');
  const [term, setTerm] = useState('');

  const query = {
    ...(status === 'all' ? {} : { status }),
    ...(workflow === 'all' ? {} : { workflow }),
    ...(term.trim() ? { search: term.trim() } : {}),
  };

  const { data, isLoading, isError, error } = useRuns(query);
  const { data: workflows = [] } = useWorkflows();

  if (isLoading) {
    return <LoadingPage label="Loading runs…" />;
  }

  if (isError) {
    return (
      <ErrorPage
        title="Could not load runs"
        message={error instanceof Error ? error.message : 'The API did not respond.'}
      />
    );
  }

  const runs = data?.items ?? [];

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">All runs</div>
          <div className="page-sub">
            Every execution across every workflow, newest first. Search by run id or business key
            when someone asks &ldquo;what happened to order 5518?&rdquo;.
          </div>
        </div>
        <div className="page-actions">
          <a className="btn btn-secondary" href={`/api${runsApi.exportUrl(query)}`}>
            <Icon name="download" size={14} /> Export
          </a>
        </div>
      </div>

      <div className="filterbar">
        <div className="search" style={{ width: 280 }}>
          <Icon name="search" size={14} />
          <input
            placeholder="Run id, business key, step…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
        <select className="select" value={workflow} onChange={(e) => setWorkflow(e.target.value)}>
          <option value="all">All workflows</option>
          {workflows.map((w) => (
            <option key={w.slug} value={w.slug}>
              {w.slug}
            </option>
          ))}
        </select>
        {STATUSES.map((s) => (
          <button
            key={s}
            className={`pick-btn ${status === s ? 'is-active' : ''}`}
            style={{ height: 34, padding: '0 12px' }}
            onClick={() => setStatus(s)}
          >
            <span className="pick-title" style={{ fontSize: 12.5 }}>
              {s === 'all' ? 'Any status' : s === 'timedout' ? 'Timed out' : s[0].toUpperCase() + s.slice(1)}
            </span>
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }} className="tiny muted">
          {runs.length} runs
        </div>
      </div>

      <RunsTable
        runs={runs}
        showWorkflow
        onOpen={(run) => navigate(`/workflows/${run.workflowSlug}/runs/${run.id}`)}
        emptyTitle={term || status !== 'all' ? 'Nothing matches those filters' : 'No runs recorded yet'}
        emptySub={
          term || status !== 'all'
            ? 'Try clearing the search or widening the status filter.'
            : 'Runs appear here the moment any workflow is triggered.'
        }
      />
    </div>
  );
}
